import React, {
  forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import { LOADING_TEXT } from './copy';
import { Badge, EmptyState, Icon, SearchInput, Skeleton, Switch, Tabs } from './ui';

// Strip ANSI escape codes from text
const stripAnsi = (str) => {
  if (!str) return str;
  // Match all ANSI escape sequences: ESC[ ... m (SGR), ESC[ ... other codes
  // eslint-disable-next-line no-control-regex
  return str.replace(/\x1b\[[0-9;]*[a-zA-Z]/g, '').replace(/\u001b\[[0-9;]*[a-zA-Z]/g, '');
};

// Where the LIVE part of the window comes from; the cold archive is always included (see
// UserLogs). `value` stays "openpaw": it is the backend log-source identifier, and only
// the label is rebranded.
const SOURCE_TABS = [
  { value: 'openpaw', label: 'Agent', icon: 'bot' },
  { value: 'machine', label: 'Host machine', icon: 'server' },
];

const LEVEL_OPTIONS = [
  { value: 'all', label: 'All levels' },
  { value: 'ERROR', label: 'Error' },
  { value: 'WARN', label: 'Warn' },
  { value: 'INFO', label: 'Info' },
  { value: 'DEBUG', label: 'Debug' },
];

// UserLogs joins the archive and the live stream with `──── label ────` lines. They are
// section headings, not log entries: drawn as dividers, kept through every filter (a
// filtered view still shows where the archive ends and live begins), and not counted.
const FENCE = /^─{3,}\s*(.*?)\s*─{3,}$/;

/* The terminal is dark in BOTH themes, because logs are read as terminal text.
   `data-theme="dark"` on it re-declares the inks, surfaces, borders and state colours
   for its subtree, so text-danger, the badges and the hairlines inside all take their
   dark-theme values. The text roles and the hover fill cannot follow on their own:
   tokens.css declares them on :root as references (`--text-3: var(--ink-3)`), which
   resolve THERE, in the page theme, and are inherited as finished colours. So they are
   re-pointed here. Without this, text-muted in the light theme is dark ink on a
   near-black surface. */
const TERMINAL_SCOPE = {
  '--text': 'var(--ink-1)',
  '--text-2': 'var(--ink-2)',
  '--text-3': 'var(--ink-3)',
  '--text-4': 'var(--ink-4)',
  '--surface-hover': 'var(--ink-7)',
};

const TERMINAL = { ...TERMINAL_SCOPE, height: 'clamp(320px, 62vh, 720px)', maxHeight: 'none' };

// Fixed columns in `ch`, so they line up row to row in a monospaced face at any size.
const COL_TIME = { width: '12.5ch' };
const COL_LEVEL = { width: '7.5ch' };
const COL_TARGET = { width: '24ch' };
const CHEVRON = { width: '1.2em', height: '1.6em' };
const ROW = { padding: 'var(--sp-1) var(--sp-3)', columnGap: 'var(--sp-3)', rowGap: 0 };
// Wrapped, the message drops below the fixed columns once it has less than 24ch beside
// them, which is what keeps a 390px screen readable. Unwrapped, it keeps its spacing and
// the terminal scrolls sideways.
const MSG_WRAP = { flex: '1 1 24ch', minWidth: 0 };
const MSG_NOWRAP = { flex: '1 0 auto', whiteSpace: 'pre' };
const TRACK = { padding: 'var(--sp-1) 0' };
const TRACK_NOWRAP = { ...TRACK, width: 'max-content', minWidth: '100%' };
const RULE_TOP = { borderTop: '1px solid var(--border-subtle)' };
const OPEN = { background: 'var(--surface-hover)' };
// Indented to the time column: row inset + chevron + the toggle's own gap.
const DETAILS = { padding: 'var(--sp-1) var(--sp-3) var(--sp-3) calc(var(--sp-3) + 1.2em + var(--sp-2))' };
const RAW = { maxHeight: 200, overflowY: 'auto' };
const FENCE_ROW = { padding: 'var(--sp-3) var(--sp-3) var(--sp-2)' };
const FENCE_RULE = { borderTop: '1px solid var(--border)' };
const SKELETON_WIDTHS = ['72%', '54%', '88%', '63%', '41%', '79%', '58%', '67%', '48%'];

// Level colour comes from the state tokens, which the terminal scope resolves to their
// dark-theme values. ERROR and WARN get a tinted chip so they stand out while scrolling.
const LEVEL_CLASS = { INFO: 'text-ok', DEBUG: 'text-muted', TRACE: 'text-muted' };

function LevelTag({ level }) {
  if (level === 'ERROR') return <Badge tone="danger" square mono>ERROR</Badge>;
  if (level === 'WARN' || level === 'WARNING') return <Badge tone="warn" square mono>WARN</Badge>;
  return <span className={LEVEL_CLASS[level] || 'text-2'}>{level.slice(0, 5)}</span>;
}

// Parse logs into structured entries
function parseLogs(logs) {
  if (!logs || typeof logs !== 'string') return [];

  return logs.split('\n').map((line, idx) => {
    if (!line.trim()) return null;

    // Strip ANSI codes before processing
    const cleanLine = stripAnsi(line);
    if (!cleanLine.trim()) return null;

    const fence = cleanLine.trim().match(FENCE);
    if (fence) return { id: idx, divider: true, label: fence[1] };

    try {
      const parsed = JSON.parse(cleanLine);
      // Fluent Bit cold-archive record: the real Rust tracing line is nested in
      // `.log` (the top-level object has no level/message/fields — just log/stream/
      // kubernetes). Unwrap it so the row shows the correct level + the actual
      // message instead of an empty INFO row. `raw` stays the full JSON so the
      // expanded view still shows the k8s metadata (pod, node, image, ...).
      if (typeof parsed.log === 'string' && parsed.level === undefined && parsed.message === undefined) {
        const inner = stripAnsi(parsed.log).replace(/\s+$/, '');
        // `timestamp LEVEL target::path: message …` — target may contain `::`, so
        // split on `:` + whitespace (the `: ` separator, not the `::` in the path).
        const m = inner.match(/^(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)\s+(TRACE|DEBUG|INFO|WARN|ERROR)\s+([\w:]+):\s+([\s\S]*)$/);
        if (m) {
          return { id: idx, timestamp: m[1], level: m[2].toUpperCase(), target: m[3], message: m[4], fields: {}, raw: cleanLine };
        }
        // No standard tracing prefix — infer level from keywords, show the whole line.
        const level = /\b(ERROR|FATAL|PANIC)\b/i.test(inner) ? 'ERROR'
                    : /\bWARN(ING)?\b/i.test(inner) ? 'WARN'
                    : /\bDEBUG\b/i.test(inner) ? 'DEBUG'
                    : /\bTRACE\b/i.test(inner) ? 'TRACE' : 'INFO';
        return {
          id: idx,
          timestamp: parsed.time || parsed.timestamp || '',
          level,
          target: parsed.kubernetes?.container_name || '',
          message: inner || '(empty log line)',
          fields: {},
          raw: cleanLine,
        };
      }
      return {
        id: idx,
        timestamp: parsed.timestamp || '',
        level: (parsed.level || 'INFO').toUpperCase(),
        message: parsed.fields?.message || parsed.message || '',
        target: parsed.target || '',
        fields: parsed.fields || {},
        raw: cleanLine,
      };
    } catch {
      // Non-JSON line - try to parse timestamp and level from plain text
      // Format: 2026-04-05T03:46.235132Z  INFO openpaw_runtime::agent: Message
      // The target is a Rust module path, so it may itself contain `::`. `[^:]+` stopped at
      // the first colon and put the rest of the path into the message (`:agent: …`).
      const match = cleanLine.match(/^(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)\s+(TRACE|DEBUG|INFO|WARN|ERROR)\s+((?:[\w-]+::)*[\w-]+):\s*(.*)$/);
      if (match) {
        return {
          id: idx,
          timestamp: match[1],
          level: match[2].toUpperCase(),
          target: match[3],
          message: match[4],
          fields: {},
          raw: cleanLine,
        };
      }
      // Fallback for unparseable lines
      return {
        id: idx,
        timestamp: '',
        level: 'INFO',
        message: cleanLine,
        target: '',
        fields: {},
        raw: cleanLine,
      };
    }
  }).filter(Boolean);
}

// Format timestamp for display
function formatTime(ts) {
  if (!ts) return '';
  const date = new Date(ts);
  // An unparseable stamp is an Invalid Date, not an exception, so it is checked here.
  if (Number.isNaN(date.getTime())) {
    const s = String(ts);
    return s.split('T')[1]?.split('.')[0] || s;
  }
  return `${date.toLocaleTimeString('en-GB', { hour12: false })}.${String(date.getMilliseconds()).padStart(3, '0')}`;
}

const plural = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

// Format fields for display (excluding message which is shown separately)
function formatFields(fields, truncate = true) {
  const filtered = { ...fields };
  delete filtered.message;
  const entries = Object.entries(filtered);
  if (entries.length === 0) return '';
  return entries.map(([k, v]) => {
    const val = truncate && typeof v === 'string' && v.length > 80 ? v.slice(0, 80) + '…' : v;
    return `${k}=${JSON.stringify(val)}`;
  }).join(' ');
}

function Fence({ label }) {
  return (
    <div role="separator" aria-label={label} className="row gap-3 text-muted text-xs fw-600" style={FENCE_ROW}>
      <span className="grow" style={FENCE_RULE} />
      <span className="text-center">{label}</span>
      <span className="grow" style={FENCE_RULE} />
    </div>
  );
}

// Expanded row: every field, then the raw line (pretty-printed when it is JSON).
function EntryDetails({ log }) {
  const fields = Object.entries(log.fields);
  let raw = log.raw;
  try {
    raw = JSON.stringify(JSON.parse(log.raw), null, 2);
  } catch {
    // Not JSON: show the line as it arrived.
  }
  return (
    <div className="stack gap-3" style={DETAILS}>
      {fields.length > 0 && (
        <div>
          <div className="overline mb-1">Fields</div>
          <dl className="kv text-sm">
            {fields.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="break">{k}</dt>
                <dd className="pre-wrap break">{typeof v === 'string' ? v : JSON.stringify(v, null, 2)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      )}
      <div>
        <div className="overline mb-1">Raw</div>
        <pre className="code-block pre-wrap break m-0" style={RAW}>{raw}</pre>
      </div>
    </div>
  );
}

/* One entry. The toggle is a real button covering the chevron, time and level columns;
   the target and message stay outside it, as plain text that can be selected and copied.
   (The whole row used to be the click target, so selecting text in a line expanded it.) */
function LogEntry({ log, expanded, onToggle, onFocus, tabbable, wrap, msgId, separated }) {
  const fields = expanded ? '' : formatFields(log.fields, true);
  const dim = log.level === 'DEBUG' || log.level === 'TRACE';
  const style = separated || expanded ? { ...(separated ? RULE_TOP : null), ...(expanded ? OPEN : null) } : undefined;
  return (
    <div style={style}>
      <div className={wrap ? 'row-wrap row-top' : 'row row-top'} style={ROW}>
        <button
          type="button"
          data-log-toggle
          className="row row-top shrink-0"
          tabIndex={tabbable ? 0 : -1}
          aria-expanded={expanded}
          aria-describedby={msgId}
          onClick={onToggle}
          onFocus={onFocus}
        >
          <span className="row center text-faint" style={CHEVRON}>
            <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={12} />
          </span>
          <span className="text-muted" style={COL_TIME}>{formatTime(log.timestamp)}</span>
          <span style={COL_LEVEL}><LevelTag level={log.level} /></span>
        </button>
        <span className="text-muted truncate shrink-0 hide-sm" style={COL_TARGET} title={log.target || undefined}>
          {log.target}
        </span>
        <span
          id={msgId}
          className={[wrap ? 'pre-wrap break' : '', dim ? 'text-muted' : ''].filter(Boolean).join(' ') || undefined}
          style={wrap ? MSG_WRAP : MSG_NOWRAP}
        >
          {log.message}
          {fields && <span className="text-muted">{`  ${fields}`}</span>}
        </span>
      </div>
      {expanded && <EntryDetails log={log} />}
    </div>
  );
}

/**
 * LogViewer component with:
 * - Color-coded log levels
 * - Left-aligned text
 * - Search/filter functionality
 * - Parsed JSON display
 * - Auto-scroll to bottom (latest logs)
 * - Source selector (OpenPaw/Host)
 * - Line wrapping on or off
 */
const LogViewer = forwardRef(({ logs, loading, scrollToBottom = true, logSource, onSourceChange }, ref) => {
  const [filter, setFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [wrap, setWrap] = useState(true);
  const containerRef = useRef(null);
  const [userScrolled, setUserScrolled] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  // Roving tabindex: exactly one row toggle is in the Tab order, so Tab enters the log
  // once and the arrow keys walk it, instead of Tab stopping on every one of 1000 lines.
  const [focusId, setFocusId] = useState(null);
  const uid = useId();

  const scrollToEnd = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({ scrollToEnd }));

  const parsedLogs = useMemo(() => parseLogs(logs), [logs]);

  // Filter logs based on search and level
  const filteredLogs = useMemo(() => parsedLogs.filter((log) => {
    if (log.divider) return true;

    // Level filter
    if (levelFilter !== 'all' && log.level !== levelFilter) return false;

    // Text search
    if (filter) {
      const searchLower = filter.toLowerCase();
      const searchable = `${log.message} ${log.target} ${JSON.stringify(log.fields)}`.toLowerCase();
      if (!searchable.includes(searchLower)) return false;
    }

    return true;
  }), [parsedLogs, filter, levelFilter]);

  const counts = useMemo(() => {
    let entries = 0;
    let errors = 0;
    let warnings = 0;
    parsedLogs.forEach((l) => {
      if (l.divider) return;
      entries += 1;
      if (l.level === 'ERROR') errors += 1;
      else if (l.level === 'WARN' || l.level === 'WARNING') warnings += 1;
    });
    return { entries, errors, warnings };
  }, [parsedLogs]);

  const shown = useMemo(() => filteredLogs.filter((l) => !l.divider).length, [filteredLogs]);

  // Auto-scroll to bottom when logs change (if user hasn't scrolled up)
  useEffect(() => {
    if (scrollToBottom && containerRef.current && !userScrolled && !loading) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [filteredLogs, scrollToBottom, loading, userScrolled, wrap]);

  // Track user scroll to disable auto-scroll when they scroll up
  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    // Consider "at bottom" if within 50px of bottom
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    setUserScrolled(!atBottom);
  };

  // Toggle expanded state for a log entry
  const toggleExpanded = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const jumpToLatest = () => {
    setUserScrolled(false);
    scrollToEnd();
  };

  const clearFilters = () => {
    setFilter('');
    setLevelFilter('all');
  };

  // The view opens scrolled to the newest line, so that is where Tab lands first.
  const lastEntryId = useMemo(() => {
    for (let i = filteredLogs.length - 1; i >= 0; i -= 1) {
      if (!filteredLogs[i].divider) return filteredLogs[i].id;
    }
    return null;
  }, [filteredLogs]);
  const tabbableId = filteredLogs.some((l) => l.id === focusId && !l.divider) ? focusId : lastEntryId;

  const onTerminalKeyDown = (e) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const toggles = Array.from(containerRef.current?.querySelectorAll('[data-log-toggle]') || []);
    const i = toggles.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    const next = e.key === 'ArrowDown' ? toggles[i + 1]
      : e.key === 'ArrowUp' ? toggles[i - 1]
        : e.key === 'Home' ? toggles[0] : toggles[toggles.length - 1];
    next?.focus();
  };

  const isFiltered = !!filter || levelFilter !== 'all';

  return (
    <div>
      <div className="toolbar">
        {logSource !== undefined && onSourceChange && (
          <Tabs tabs={SOURCE_TABS} value={logSource} onChange={onSourceChange} label="Log source" />
        )}
        <SearchInput value={filter} onChange={setFilter} placeholder="Search logs…" label="Search logs" />
        <select
          className="form-input"
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          aria-label="Filter by level"
        >
          {LEVEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="toolbar-spacer" />
        <Switch checked={wrap} onChange={setWrap} label="Wrap lines" />
      </div>

      {/* Held at the height of the small button, so the terminal does not jump when
          "Jump to latest" appears. */}
      <div className="row-wrap gap-3 mb-2 text-sm" style={{ minHeight: 'var(--control-h-sm)' }}>
        {loading ? (
          <span className="text-muted">{LOADING_TEXT}</span>
        ) : (
          <>
            <span className="text-muted num" role="status">
              Showing {shown} of {counts.entries} entries
            </span>
            <span className="text-danger num">{plural(counts.errors, 'error')}</span>
            <span className="text-warn num">{plural(counts.warnings, 'warning')}</span>
          </>
        )}
        {userScrolled && !loading && (
          <button type="button" className="btn btn-secondary btn-sm ml-auto" onClick={jumpToLatest}>
            <Icon name="arrowDown" /> Jump to latest
          </button>
        )}
      </div>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        onKeyDown={onTerminalKeyDown}
        className="logs-container p-0"
        data-theme="dark"
        role="region"
        aria-label="Log output"
        aria-busy={loading || undefined}
        style={TERMINAL}
      >
        {loading ? (
          <div className="stack gap-2" style={{ padding: 'var(--sp-4)' }}>
            <span className="sr-only">Loading logs</span>
            {SKELETON_WIDTHS.map((w, i) => <Skeleton key={i} width={w} height={10} />)}
          </div>
        ) : shown === 0 ? (
          <EmptyState
            icon={isFiltered ? 'search' : 'terminal'}
            title={isFiltered ? 'No logs match the current filter' : 'No logs available'}
            action={isFiltered ? (
              <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            ) : null}
          />
        ) : (
          <div style={wrap ? TRACK : TRACK_NOWRAP}>
            {filteredLogs.map((log, i) => (log.divider ? (
              <Fence key={log.id} label={log.label} />
            ) : (
              <LogEntry
                key={log.id}
                log={log}
                expanded={expandedIds.has(log.id)}
                onToggle={() => toggleExpanded(log.id)}
                onFocus={() => setFocusId(log.id)}
                tabbable={log.id === tabbableId}
                wrap={wrap}
                msgId={`${uid}-msg-${log.id}`}
                separated={i > 0 && !filteredLogs[i - 1].divider}
              />
            )))}
          </div>
        )}
      </div>
    </div>
  );
});

LogViewer.displayName = 'LogViewer';

export default LogViewer;
